import { inject, Injectable } from '@angular/core';
import { Action, Actions, ofActionDispatched, State, StateContext } from '@ngxs/store';
import { catchError, EMPTY, Observable, switchMap, takeUntil, tap } from 'rxjs';
import { webSocket } from 'rxjs/webSocket';
import { AutoRenewNotificationsService } from '@pages/subscriptions/services/auto-renew-notifications.service';
import {
  AutoRenewNotification,
  AutoRenewNotificationEvent,
} from '@pages/subscriptions/models/auto-renew-notifications/auto-renew-notification';
import { LoadAccountAutoRenewStatus } from '@pages/subscriptions/store/actions/account-auto-renew.actions';
import {
  AutoRenewStatusChanged,
  StartListeningAutoRenewNotifications,
  StopListeningAutoRenewNotifications,
} from '@pages/subscriptions/store/actions/auto-renew-notifications.actions';
import { autoRenewErrorToastKey, autoRenewSuccessToastKey } from '@pages/subscriptions/utils/auto-renew-toast-keys.utility';
import { ToastService } from '@core/toast/toast.service';

@State<void>({
  name: 'autoRenewNotifications',
})
@Injectable()
export class AutoRenewNotificationsState {
  private readonly autoRenewNotificationsService = inject(AutoRenewNotificationsService);
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);

  @Action(StartListeningAutoRenewNotifications, { cancelUncompleted: true })
  startListeningAutoRenewNotifications(context: StateContext<void>): Observable<AutoRenewNotification> {
    const stopListening$ = this.actions$.pipe(ofActionDispatched(StopListeningAutoRenewNotifications));

    return this.autoRenewNotificationsService.getPublishSubscribeClientAccessUrl().pipe(
      switchMap(url => this.listenStatusUpdates$(url, context)),
      catchError(() => EMPTY),
      takeUntil(stopListening$)
    );
  }

  private listenStatusUpdates$(url: string, context: StateContext<void>): Observable<AutoRenewNotification> {
    return webSocket<AutoRenewNotification>(url).pipe(tap(notification => this.handleStatusNotification(notification, context)));
  }

  private handleStatusNotification({ eventName, isEnabled, isSucceeded }: AutoRenewNotification, context: StateContext<void>): void {
    switch (eventName) {
      case AutoRenewNotificationEvent.AccountStatusUpdated:
        context.dispatch([new LoadAccountAutoRenewStatus(), new AutoRenewStatusChanged()]);
        break;
      case AutoRenewNotificationEvent.AssetsStatusUpdated:
        context.dispatch(new AutoRenewStatusChanged());
        break;
    }

    if (isSucceeded) {
      this.toastService.showSuccessToast(autoRenewSuccessToastKey(isEnabled));
    } else {
      this.toastService.showErrorToast(autoRenewErrorToastKey(isEnabled));
    }
  }
}
